import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Menu,
  X,
  Home,
  ShoppingBag,
  Info,
  Phone,
  ChevronDown,
  ChevronUp,
  ShoppingCart,
  Search,
  Shirt,
  Sparkles,
  Baby,
  Box,
  BriefcaseBusiness,
  Footprints,
  Grid3X3,
  Facebook,
  Instagram,
  Twitter
} from "lucide-react";
import SearchModal from "./SearchModal";
import { useCart } from "../context/CartContext";

function Navbar() {
  const [isOpen, setIsOpen] = useState(false); // mobile sidebar
  const [dropdownOpen, setDropdownOpen] = useState(false); // categories dropdown
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { cartItems } = useCart();
  const totalItems = cartItems.reduce((sum, i) => sum + i.quantity, 0);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleSidebar = () => setIsOpen(!isOpen);
  const toggleDropdown = () => setDropdownOpen(!dropdownOpen);
  const closeSidebar = () => {
    setIsOpen(false);
    setDropdownOpen(false);
  };

  return (
    <>
      {/* ---------- TOP NAV ----------- */}
      <nav
        className={`fixed w-full z-50 top-0 left-0 transition-all duration-300 ${
          scrolled ? "bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100" : "bg-white border-b border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          {/* logo + mobile toggle */}
          <div className="relative flex flex-1 md:flex-none justify-center md:justify-start items-center">
            {/* Mobile menu button - absolute on left */}
            <button
              onClick={toggleSidebar}
              className="absolute left-0 md:hidden text-gray-900 hover:text-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-200 p-2 rounded-full transition-colors bg-gray-50 border border-gray-100 hover:border-yellow-200 hover:bg-yellow-50"
            >
              <Menu size={20} />
            </button>

            {/* Logo + Name */}
            <Link
              to="/"
              className="flex items-center gap-3 select-none outline-none group rounded-xl p-1"
              aria-label="Homepage"
            >
              <img
                src="./PakFashions-logo.jpg"
                alt="PakFashion Logo"
                className="w-10 h-10 rounded-full border border-gray-200 object-cover shadow-sm group-hover:shadow-md transition-shadow hidden md:block"
              />
              <span className="text-xl md:text-2xl font-black text-gray-900 tracking-tight text-center">
                PAKFASHIONS
              </span>
            </Link>
          </div>

          {/* ----------- DESKTOP NAV ----------- */}
          <div className="hidden md:flex items-center space-x-6 text-gray-700">
            <NavLink to="/" label="Home" icon={<Home size={18} />} />
            <NavLink to="/shop" label="Shop" icon={<ShoppingBag size={18} />} />

            {/* categories dropdown */}
            <div className="relative">
              <button
                onClick={toggleDropdown}
                className="flex items-center gap-1.5 px-3 py-2 text-gray-600 hover:text-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-200 rounded-lg transition-colors font-medium"
                aria-haspopup="true"
                aria-expanded={dropdownOpen}
                aria-controls="categories-menu"
              >
                Categories
                {dropdownOpen ? (
                  <ChevronUp size={16} />
                ) : (
                  <ChevronDown size={16} />
                )}
              </button>

              <div
                id="categories-menu"
                role="menu"
                onClick={() => setDropdownOpen(false)}
                className={`absolute bg-white shadow-xl border border-gray-100 rounded-2xl w-64 mt-3 z-50 overflow-hidden
                  transition-all duration-200 ease-in-out origin-top-left
                  ${
                    dropdownOpen
                      ? "opacity-100 scale-100 translate-y-0 pointer-events-auto"
                      : "opacity-0 scale-95 -translate-y-2 pointer-events-none"
                  }
                `}
              >
                <div className="p-2 space-y-1">
                  <DropdownLink
                    to="/category/Men's Clothing"
                    label="Men's Clothing"
                    icon={<Shirt size={16} className="text-gray-400" />}
                  />
                  <DropdownLink
                    to="/category/Women's Clothing"
                    label="Women's Clothing"
                    icon={<Sparkles size={16} className="text-gray-400" />}
                  />
                  <DropdownLink
                    to="/category/Kids' Clothing"
                    label="Kids' Clothing"
                    icon={<Baby size={16} className="text-gray-400" />}
                  />
                  <DropdownLink
                    to="/category/Shoes"
                    label="Shoes"
                    icon={<Footprints size={16} className="text-gray-400" />}
                  />
                  <DropdownLink
                    to="/category/Bags & Accessories"
                    label="Bags & Accessories"
                    icon={<BriefcaseBusiness size={16} className="text-gray-400" />}
                  />
                  <DropdownLink
                    to="/category/Clearance Sale"
                    label="Clearance Sale"
                    icon={<Box size={16} className="text-gray-400" />}
                  />
                </div>
              </div>
            </div>

            <NavLink to="/about" label="About" icon={<Info size={18} />} />
            <NavLink to="/contact" label="Contact" icon={<Phone size={18} />} />
          </div>

          {/* search + cart */}
          <div className="flex items-center space-x-2 md:space-x-4">
            <button
              onClick={() => setIsSearchOpen(true)}
              className="text-gray-600 hover:text-yellow-600 hover:bg-yellow-50 focus:outline-none focus:ring-2 focus:ring-yellow-200 p-2.5 rounded-full transition-all"
              aria-label="Open search"
            >
              <Search size={22} />
            </button>
            <Link
              to="/cart"
              className="relative text-gray-600 hover:text-yellow-600 hover:bg-yellow-50 focus:outline-none focus:ring-2 focus:ring-yellow-200 p-2.5 rounded-full transition-all"
              aria-label="Shopping cart"
            >
              <ShoppingCart size={22} />
              {totalItems > 0 && (
                <span
                  className="absolute top-0.5 right-0 bg-yellow-500 text-white rounded-full text-[10px] font-bold flex items-center justify-center w-[18px] h-[18px] shadow-sm ring-2 ring-white"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  {totalItems}
                </span>
              )}
            </Link>
          </div>
        </div>
      </nav>

      {/* ------------- MOBILE SIDEBAR ------------- */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm z-40 transition-opacity"
          onClick={closeSidebar}
          aria-hidden="true"
        />
      )}
      <aside
        className={`fixed top-0 left-0 h-full w-[300px] bg-white/95 backdrop-blur-2xl shadow-2xl z-50 transform transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] rounded-r-[2rem] border-r border-gray-100/50 flex flex-col ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        aria-label="Mobile menu"
        role="menu"
      >
        <div className="flex justify-between items-center p-6 border-b border-gray-100/50">
          <span className="text-2xl font-black text-gray-900 select-none tracking-tight">Menu</span>
          <button
            onClick={closeSidebar}
            className="text-gray-400 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 p-2 rounded-full transition-colors bg-gray-50 border border-gray-100"
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 pb-6 w-full custom-scrollbar">
          <nav className="flex flex-col p-4 w-full">
            <NavLinkMobile
              to="/"
              label="Home"
              icon={<Home size={20} className="text-gray-400" />}
              onClick={closeSidebar}
            />
            <NavLinkMobile
              to="/shop"
              label="Shop"
              icon={<ShoppingBag size={20} className="text-gray-400" />}
              onClick={closeSidebar}
            />

            {/* mobile categories toggle */}
            <div className="my-1 px-2">
              <button
                onClick={toggleDropdown}
                className="flex items-center justify-between px-4 py-3.5 font-bold text-gray-700 hover:text-yellow-600 hover:bg-yellow-50 rounded-2xl transition-all focus:outline-none focus:ring-2 focus:ring-yellow-200 w-full group"
                aria-haspopup="true"
                aria-expanded={dropdownOpen}
              >
                <div className="flex items-center gap-3">
                  <Grid3X3 className="text-gray-400 group-hover:text-yellow-500 transition-colors" size={20} />
                  Categories
                </div>
                {dropdownOpen ? (
                  <ChevronUp size={20} className="text-gray-400" />
                ) : (
                  <ChevronDown size={20} className="text-gray-400" />
                )}
              </button>
            </div>

            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                dropdownOpen ? "max-h-[400px] opacity-100 mb-2 mt-1" : "max-h-0 opacity-0"
              }`}
            >
              <div className="pl-6 border-l-2 border-gray-100 ml-8 space-y-1">
                <NavSubLinkMobile
                  to="/category/Men's Clothing"
                  label="Men's Clothing"
                  onClick={closeSidebar}
                />
                <NavSubLinkMobile
                  to="/category/Women's Clothing"
                  label="Women's Clothing"
                  onClick={closeSidebar}
                />
                <NavSubLinkMobile
                  to="/category/Kids' Clothing"
                  label="Kids' Clothing"
                  onClick={closeSidebar}
                />
                <NavSubLinkMobile
                  to="/category/Shoes"
                  label="Shoes"
                  onClick={closeSidebar}
                />
                <NavSubLinkMobile
                  to="/category/Bags & Accessories"
                  label="Bags & Accessories"
                  onClick={closeSidebar}
                />
                <NavSubLinkMobile
                  to="/category/Clearance Sale"
                  label="Clearance Sale"
                  className="text-red-500 font-bold hover:text-red-600 hover:bg-red-50"
                  onClick={closeSidebar}
                />
              </div>
            </div>

            <div className="h-px bg-gradient-to-r from-gray-100 to-transparent my-3 mx-4" />

            <NavLinkMobile
              to="/about"
              label="About"
              icon={<Info size={20} className="text-gray-400" />}
              onClick={closeSidebar}
            />
            <NavLinkMobile
              to="/contact"
              label="Contact"
              icon={<Phone size={20} className="text-gray-400" />}
              onClick={closeSidebar}
            />
          </nav>
        </div>

        {/* Sidebar Footer Content */}
        <div className="mt-auto p-6 border-t border-gray-100/50 bg-gray-50/50 rounded-br-[2rem]">
          <div className="flex flex-col gap-4">
            <div className="flex justify-center space-x-4 mb-2">
              <a href="https://web.facebook.com/PAKFASHIONSKE" target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-500 hover:text-blue-600 hover:border-blue-200 hover:bg-blue-50 transition-all shadow-sm">
                <Facebook size={18} />
              </a>
              <a href="https://www.instagram.com/pakfashionske" target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-500 hover:text-pink-600 hover:border-pink-200 hover:bg-pink-50 transition-all shadow-sm">
                <Instagram size={18} />
              </a>
              <a href="https://twitter.com/pakfashionske" target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-500 hover:text-gray-900 hover:border-gray-300 hover:bg-gray-100 transition-all shadow-sm">
                <Twitter size={18} />
              </a>
            </div>
            
            <div className="text-center">
              <p className="text-sm font-bold text-gray-900 mb-1 flex items-center justify-center gap-1.5">
                <Sparkles size={14} className="text-yellow-500" /> Fashion with Purpose
              </p>
              <p className="text-xs text-gray-500 font-medium">
                © {new Date().getFullYear()} Pak Fashions.<br />All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </aside>

      {/* ------------- SEARCH MODAL ------------- */}
      <SearchModal
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
      />
    </>
  );
}

function NavLink({ to, label, icon }) {
  return (
    <Link
      to={to}
      className="flex items-center gap-2 text-gray-600 hover:text-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-200 rounded-lg px-3 py-2 transition-colors font-medium group relative"
    >
      {icon && <span className="opacity-80 group-hover:opacity-100">{icon}</span>}
      <span>{label}</span>
      <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-yellow-500 transition-all duration-300 group-hover:w-[80%] rounded-full opacity-0 group-hover:opacity-100" aria-hidden="true" />
    </Link>
  );
}

function DropdownLink({ to, label, icon }) {
  return (
    <Link
      to={to}
      role="menuitem"
      className="flex items-center gap-3 px-4 py-2.5 text-gray-700 hover:text-yellow-700 hover:bg-yellow-50 focus:bg-yellow-50 focus:outline-none rounded-xl transition-colors font-medium"
    >
      {icon}
      {label}
    </Link>
  );
}

function NavLinkMobile({ to, label, icon, onClick }) {
  return (
    <div className="px-2 my-1">
      <Link
        to={to}
        onClick={onClick}
        className="flex items-center gap-3 px-4 py-3.5 font-bold text-gray-700 hover:text-yellow-600 hover:bg-yellow-50 focus:outline-none focus:ring-2 focus:ring-yellow-200 rounded-2xl transition-all group w-full"
      >
        {icon && <span className="group-hover:text-yellow-500 transition-colors">{icon}</span>}
        <span>{label}</span>
      </Link>
    </div>
  );
}

function NavSubLinkMobile({ to, label, onClick, className = "text-gray-600 hover:text-yellow-600 hover:bg-yellow-50 font-medium" }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`block px-4 py-2.5 rounded-xl transition-all mb-1 ${className}`}
    >
      {label}
    </Link>
  );
}

export default Navbar;
